import { useMemo, useState } from "react";
import { BarChart3, Users } from "lucide-react";
import { Link } from "react-router";
import { useListClientInvoicesQuery } from "../../redux/apis/billingApi";
import { cn } from "../components/ui/utils";
import { DashboardRevenuePanel } from "../components/dashboard/DashboardRevenuePanel";
import { buildDashboardClientOptions } from "../utils/dashboardRevenue";

const REVENUE_VIEWS = [
  { id: "current", label: "Current", description: "Recognized revenue (excludes drafts)" },
  {
    id: "projected",
    label: "Projected",
    description:
      "YTD projected as prior-month average × 12; current month uses business-day run rate",
  },
];

function RevenueViewToggle({ value, onChange, ariaLabel = "Revenue view" }) {
  return (
    <div
      className="inline-flex rounded-xl border border-gray-200/80 bg-gray-50/80 p-1 shadow-sm backdrop-blur-sm dark:border-gray-700/80 dark:bg-gray-900/60"
      role="tablist"
      aria-label={ariaLabel}
    >
      {REVENUE_VIEWS.map((view) => {
        const active = value === view.id;
        return (
          <button
            key={view.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(view.id)}
            className={cn(
              "app-btn-plain rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200",
              active
                ? "bg-white text-[#1a3268] shadow-sm ring-1 ring-gray-200/80 dark:bg-gray-800 dark:text-blue-100 dark:ring-gray-600/60"
                : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            )}
          >
            {view.label}
          </button>
        );
      })}
    </div>
  );
}

function Dashboard() {
  const [revenueView, setRevenueView] = useState("current");
  const [clientRevenueView, setClientRevenueView] = useState("current");
  const [selectedClientCustgroup, setSelectedClientCustgroup] = useState("");

  const {
    data: invoices = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useListClientInvoicesQuery();

  const clientOptions = useMemo(() => buildDashboardClientOptions(invoices), [invoices]);

  const selectedClientLabel = useMemo(() => {
    if (!selectedClientCustgroup) {
      return "";
    }
    return (
      clientOptions.find(
        (client) => client.custgroup.toLowerCase() === selectedClientCustgroup.toLowerCase()
      )?.label ?? selectedClientCustgroup
    );
  }, [clientOptions, selectedClientCustgroup]);

  const activeViewMeta = REVENUE_VIEWS.find((view) => view.id === revenueView);
  const activeClientViewMeta = REVENUE_VIEWS.find((view) => view.id === clientRevenueView);
  const loading = isLoading || isFetching;
  const initialLoading = isLoading;

  const loadError =
    isError &&
    (typeof error?.data?.detail === "string"
      ? error.data.detail
      : "Unable to load revenue data.");

  return (
    <div className="min-h-full bg-gradient-to-b from-gray-50/80 via-white to-white p-6 sm:p-8 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900">
      <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#1a3268]/15 bg-[#1a3268]/5 px-3 py-1 text-xs font-medium text-[#1a3268] dark:border-blue-400/20 dark:bg-blue-500/10 dark:text-blue-200">
            <BarChart3 className="h-3.5 w-3.5" />
            Revenue overview
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            Dashboard
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            Calendar-year revenue from invoice amounts grouped by service month. Projected YTD
            annualizes the average of prior months (× 12); the current month forecast uses a
            business-day run rate.
          </p>
        </div>

        <div className="flex flex-col items-start gap-2 sm:items-end">
          <RevenueViewToggle value={revenueView} onChange={setRevenueView} />
          <p className="max-w-xs text-right text-xs text-gray-500 dark:text-gray-400">
            {activeViewMeta?.description}
          </p>
        </div>
      </div>

      {loadError ? (
        <div className="mb-6 rounded-xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 backdrop-blur-sm dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
          {loadError}
        </div>
      ) : null}

      <section aria-label="All clients revenue">
        <DashboardRevenuePanel
          invoices={invoices}
          revenueView={revenueView}
          loading={loading}
          initialLoading={initialLoading}
          scopeLabel="All clients"
        />
      </section>

      <div className="mt-8 flex justify-end">
        <Link
          to="/invoices"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#1a3268] transition-colors hover:text-[#223f82] dark:text-blue-300 dark:hover:text-blue-200"
        >
          View all invoices
          <span aria-hidden>→</span>
        </Link>
      </div>

      <section
        aria-label="Client revenue"
        className="mt-12 rounded-2xl border border-gray-200/80 bg-white/60 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700/80 dark:bg-gray-900/50 sm:p-8"
      >
        <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-violet-500/20 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-800 dark:text-violet-200">
              <Users className="h-3.5 w-3.5" />
              Client revenue
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              Revenue by Client
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
              Select a client to view the same current and projected metrics for their invoices
              only.
            </p>
          </div>

          <div className="flex flex-col items-start gap-2 sm:items-end">
            <RevenueViewToggle
              value={clientRevenueView}
              onChange={setClientRevenueView}
              ariaLabel="Client revenue view"
            />
            <p className="max-w-xs text-right text-xs text-gray-500 dark:text-gray-400">
              {activeClientViewMeta?.description}
            </p>
          </div>
        </div>

        <div className="mb-6 w-full sm:max-w-xs">
          <label htmlFor="dashboard-client-select" className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Client
          </label>
          <select
            id="dashboard-client-select"
            value={selectedClientCustgroup}
            onChange={(e) => setSelectedClientCustgroup(e.target.value)}
            disabled={loading || clientOptions.length === 0}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">Select a client…</option>
            {clientOptions.map((client) => (
              <option key={client.custgroup} value={client.custgroup}>
                {client.label}
                {client.label !== client.custgroup ? ` (${client.custgroup})` : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedClientCustgroup ? (
          <>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Showing metrics for{" "}
              <strong className="font-medium text-gray-900 dark:text-gray-100">
                {selectedClientLabel}
              </strong>
            </p>
            <DashboardRevenuePanel
              invoices={invoices}
              revenueView={clientRevenueView}
              loading={loading}
              initialLoading={initialLoading}
              clientCustgroup={selectedClientCustgroup}
              scopeLabel={selectedClientLabel}
            />
          </>
        ) : (
          <DashboardRevenuePanel
            invoices={invoices}
            revenueView={clientRevenueView}
            loading={loading}
            emptyMessage={
              loading
                ? "Loading clients…"
                : clientOptions.length === 0
                  ? "No clients with invoices found."
                  : "Select a client above to view their revenue metrics."
            }
          />
        )}
      </section>
    </div>
  );
}

export { Dashboard };
